import type { z } from 'zod'
import {
  AccountSchema,
  AdminDefinitionsResponseSchema,
  AdminIntakeDetailSchema,
  AdminIntakePageSchema,
  AdminIntakeSchema,
  ApiErrorSchema,
  AuditResponseSchema,
  FollowupResponseSchema,
  IntakeAddressSchema,
  IntakeAddressSuggestionsSchema,
  IntakeSaveResultSchema,
  IntakeStateSchema,
  IntakeUploadResultSchema,
  type Account,
  type AdminDefinitionsResponse,
  type AdminIntake,
  type AdminIntakeDetail,
  type AdminIntakePage,
  type AuditResponse,
  type FollowupResponse,
  type IntakeAddress,
  type IntakeAddressSuggestion,
  type IntakeSaveResult,
  type IntakeState,
  type IntakeUploadResult,
} from './schemas'

const API_BASE = '/api'

export class ApiRequestError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
  }
}

let authFailureHandler: ((status: 401 | 403) => void) | undefined

/** Lets the session state react consistently when any protected API request fails. */
export function setAuthFailureHandler(handler: (status: 401 | 403) => void) {
  authFailureHandler = handler
}

async function apiError(res: Response, label: string): Promise<never> {
  let message = `${label} failed (HTTP ${res.status})`
  try {
    const body: unknown = await res.json()
    const parsed = ApiErrorSchema.safeParse(body)
    if (parsed.success) message = parsed.data.error
  } catch {
    // Keep the useful status fallback when the response is not JSON.
  }
  throw new ApiRequestError(message, res.status)
}

async function parseJson<S extends z.ZodType>(
  schema: S,
  res: Response,
  label: string,
): Promise<z.infer<S>> {
  const body: unknown = await res.json()
  const parsed = schema.safeParse(body)
  if (!parsed.success)
    throw new ApiRequestError(`${label} returned an unexpected response.`, res.status)
  return parsed.data
}

async function jsonRequest<S extends z.ZodType>(
  schema: S,
  path: string,
  label: string,
  init?: RequestInit,
): Promise<z.infer<S>> {
  const res = await fetch(`${API_BASE}${path}`, init)
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) authFailureHandler?.(res.status)
    return apiError(res, label)
  }
  return parseJson(schema, res, label)
}

async function noContentRequest(path: string, label: string, init?: RequestInit): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, init)
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) authFailureHandler?.(res.status)
    return apiError(res, label)
  }
}

const jsonHeaders = { 'Content-Type': 'application/json' }

export function register(email: string, password: string): Promise<Account> {
  return jsonRequest(AccountSchema, '/auth/register', 'Creating account', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ email, password }),
  })
}

export function login(email: string, password: string): Promise<Account> {
  return jsonRequest(AccountSchema, '/auth/login', 'Signing in', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ email, password }),
  })
}

export function getCurrentAccount(): Promise<Account> {
  return jsonRequest(AccountSchema, '/auth/me', 'Loading account')
}

export function logout(): Promise<void> {
  return noContentRequest('/auth/logout', 'Signing out', { method: 'POST', headers: jsonHeaders })
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return noContentRequest('/auth/change-password', 'Changing password', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })
}

export function getAdminDefinitions(): Promise<AdminDefinitionsResponse> {
  return jsonRequest(
    AdminDefinitionsResponseSchema,
    '/admin/intake-definitions',
    'Loading intake templates',
  )
}

export function listAdminIntakes(page = 1): Promise<AdminIntakePage> {
  return jsonRequest(
    AdminIntakePageSchema,
    `/admin/intakes?page=${encodeURIComponent(page)}`,
    'Loading intake links',
  )
}

export function getAdminIntake(id: number): Promise<AdminIntakeDetail> {
  return jsonRequest(
    AdminIntakeDetailSchema,
    `/admin/intakes/${encodeURIComponent(id)}`,
    'Loading intake',
  )
}

export async function createAdminIntake(
  request: { client_name: string; form_type: string; expires_at: string },
  idempotencyKey: string,
): Promise<AdminIntake> {
  return jsonRequest(AdminIntakeSchema, '/admin/intakes', 'Creating intake link', {
    method: 'POST',
    headers: { ...jsonHeaders, 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(request),
  })
}

export function revokeAdminIntake(id: number): Promise<void> {
  return noContentRequest(
    `/admin/intakes/${encodeURIComponent(id)}/revoke`,
    'Revoking intake link',
    { method: 'POST', headers: jsonHeaders },
  )
}

export function resetAdminIntake(id: number): Promise<void> {
  return noContentRequest(`/admin/intakes/${encodeURIComponent(id)}/reset`, 'Resetting intake', {
    method: 'POST',
    headers: jsonHeaders,
  })
}

export async function getIntakeState(token: string): Promise<IntakeState> {
  const res = await fetch(`${API_BASE}/intake/${encodeURIComponent(token)}`)
  if (!res.ok) return apiError(res, 'Loading intake')
  return parseJson(IntakeStateSchema, res, 'Loading intake')
}

export async function saveIntakeStep(
  token: string,
  step: number,
  data: Record<string, unknown>,
): Promise<IntakeSaveResult> {
  const res = await fetch(`${API_BASE}/intake/${encodeURIComponent(token)}/step/${step}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) return apiError(res, 'Saving this step')
  return parseJson(IntakeSaveResultSchema, res, 'Saving this step')
}

// The API validates multipart part order: `kind` must be appended before `file`.
export async function uploadIntakeDocument(
  token: string,
  kind: string,
  file: File,
): Promise<IntakeUploadResult> {
  const body = new FormData()
  body.append('kind', kind)
  body.append('file', file)
  const res = await fetch(`${API_BASE}/intake/${encodeURIComponent(token)}/upload`, {
    method: 'POST',
    body,
  })
  if (!res.ok) return apiError(res, 'Uploading document')
  return parseJson(IntakeUploadResultSchema, res, 'Uploading document')
}

export async function removeIntakeDocument(token: string, id: number): Promise<void> {
  const res = await fetch(
    `${API_BASE}/intake/${encodeURIComponent(token)}/documents/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  )
  if (!res.ok) return apiError(res, 'Removing document')
}

// Opens in a new tab as a plain link; the API streams the file back from Drive.
export function intakeDocumentFileUrl(token: string, id: number): string {
  return `${API_BASE}/intake/${encodeURIComponent(token)}/documents/${encodeURIComponent(id)}/file`
}

export async function suggestIntakeAddresses(
  token: string,
  query: string,
  session: string,
  signal?: AbortSignal,
): Promise<IntakeAddressSuggestion[]> {
  const params = new URLSearchParams({ q: query, session })
  const res = await fetch(
    `${API_BASE}/intake/${encodeURIComponent(token)}/address/suggest?${params.toString()}`,
    signal ? { signal } : undefined,
  )
  if (!res.ok) return apiError(res, 'Address suggestions')
  return (await parseJson(IntakeAddressSuggestionsSchema, res, 'Address suggestions')).suggestions
}

export async function getIntakeAddress(
  token: string,
  placeId: string,
  session: string,
): Promise<IntakeAddress> {
  const params = new URLSearchParams({ id: placeId, session })
  const res = await fetch(
    `${API_BASE}/intake/${encodeURIComponent(token)}/address/place?${params.toString()}`,
  )
  if (!res.ok) return apiError(res, 'Address lookup')
  return parseJson(IntakeAddressSchema, res, 'Address lookup')
}

export async function submitIntake(token: string): Promise<void> {
  const res = await fetch(`${API_BASE}/intake/${encodeURIComponent(token)}/submit`, {
    method: 'POST',
  })
  if (!res.ok) return apiError(res, 'Submitting intake')
}

function runReport(
  path: string,
  domain: string,
  email: string,
  label: string,
): Promise<AuditResponse> {
  return jsonRequest(AuditResponseSchema, path, label, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ domain, email }),
  })
}

export function runAudit(domain: string, email: string): Promise<AuditResponse> {
  return runReport('/audit', domain, email, 'Audit')
}

export function runSeo(domain: string, email: string): Promise<AuditResponse> {
  return runReport('/seo', domain, email, 'SEO audit')
}

export function submitFollowup(auditId: number, email: string): Promise<FollowupResponse> {
  return jsonRequest(FollowupResponseSchema, '/followup', 'Follow-up', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ audit_id: auditId, email }),
  })
}
